import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'

import { Button, Drawer } from '@ui/index'
import { IconClearClose, IconEdit } from '@img/icons'
import type { IResponseAvatar } from '@api/profile/api.types'
import { getAvatarCatalogItemUrl, getOperatorAvatarUrl, normalizeOperatorAvatarID } from '@services/operator-avatar'
import { useAvatarCatalog } from '@api/operatorAvatars'
import { reportUnexpectedClientError } from '@services/client-error.service'

import css from './avatar-picker.module.scss'

export const AvatarPicker = ({avatarID, avatarUrl, onSave}: {
    avatarID?: number
    avatarUrl?: string | null
    onSave: (avatarID: number) => Promise<IResponseAvatar>
}) => {
    const [isOpen, setOpen] = useState(false)
    const [selectedID, setSelectedID] = useState(() => normalizeOperatorAvatarID(avatarID))
    const [isSaving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [savedMessage, setSavedMessage] = useState('')
    const [folderID, setFolderID] = useState(1)
    const {items, folders, isLoading, error: catalogError, loadCatalog} = useAvatarCatalog()
    const trigger = useRef<HTMLButtonElement>(null)
    const dialog = useRef<HTMLElement>(null)
    const restoreFocus = useRef(false)
    const mounted = useRef(true)
    const pending = useRef(false)
    const dialogVersion = useRef(0)

    useEffect(() => {
        mounted.current = true
        return () => {mounted.current = false; dialogVersion.current += 1}
    }, [])

    useEffect(() => {
        if (!isOpen && !isSaving && restoreFocus.current) {
            restoreFocus.current = false
            trigger.current?.focus()
        }
    }, [isOpen, isSaving])

    const open = () => {
        if (pending.current) return
        dialogVersion.current += 1
        setSelectedID(normalizeOperatorAvatarID(avatarID))
        setError('')
        setSavedMessage('')
        setOpen(true)
        const version = dialogVersion.current
        void loadCatalog(true).then(() => {
            if (!mounted.current || version !== dialogVersion.current) return
            const catalog = useAvatarCatalog.getState()
            setFolderID(catalog.items.find(item => item.id === normalizeOperatorAvatarID(avatarID))?.folderID || catalog.folders[0]?.id || 1)
        })
    }
    const cancel = () => {
        if (pending.current) return
        dialogVersion.current += 1
        restoreFocus.current = true
        setOpen(false)
    }
    const save = () => {
        if (pending.current || isLoading || catalogError || !items.some(item => item.id === selectedID)
            || selectedID === normalizeOperatorAvatarID(avatarID)) return
        pending.current = true
        setSaving(true)
        setError('')
        const version = dialogVersion.current
        const isCurrent = () => mounted.current && version === dialogVersion.current
        let result: Promise<IResponseAvatar>
        try { result = onSave(selectedID) }
        catch (failure) { result = Promise.reject(failure) }
        void result.then(response => {
            if (!isCurrent()) return
            if (response.status !== 200) {
                setError(response.message || 'Не удалось сохранить аватар. Попробуйте ещё раз.')
                return
            }
            restoreFocus.current = true
            setOpen(false)
            setSavedMessage('Аватар сохранён.')
        }).catch(failure => {
            if (!isCurrent()) return
            reportUnexpectedClientError(failure, 'Profile.changeAvatar')
            setError('Не удалось сохранить аватар. Попробуйте ещё раз.')
        }).finally(() => {
            if (!isCurrent()) return
            pending.current = false
            setSaving(false)
        })
    }

    const keepFocusInDialog = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key !== 'Tab') return
        const items = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)') || [])
        if (!items.length) {event.preventDefault(); return}
        const first = items[0], last = items[items.length - 1]
        if (event.shiftKey && document.activeElement === first) {event.preventDefault(); last.focus()}
        else if (!event.shiftKey && document.activeElement === last) {event.preventDefault(); first.focus()}
    }

    return <>
        <button ref={trigger} type="button" className={css.trigger} onClick={open}
            aria-label="Изменить аватар" title="Изменить аватар" disabled={isSaving}>
            <img src={getOperatorAvatarUrl(avatarID, avatarUrl)} alt="Аватар оператора" />
            <span className={css.edit} aria-hidden="true"><IconEdit width="14px" height="14px" /></span>
        </button>
        <span className={css.sr_only} role="status">{savedMessage}</span>

        {isOpen && <Drawer isOpen placement="right" size="min(640px, 100vw)" maxWidth="100vw"
            isEsc={!isSaving} isWrapper={!isSaving} onCancel={cancel}>
            <section ref={dialog} className={css.dialog} role="dialog" aria-modal="true" aria-labelledby="avatar-picker-title" aria-busy={isSaving} onKeyDown={keepFocusInDialog}>
                <div className={css.header}>
                    <div><h4 id="avatar-picker-title">Выберите аватар</h4><p>Доступно {items.length} вариантов.</p></div>
                    <Button variant="icon" size="sm" isDisabled={isSaving} onClick={cancel}>
                        <span className={css.sr_only}>Закрыть выбор аватара</span>
                        <IconClearClose width="20px" height="20px" />
                    </Button>
                </div>

                <label className={css.folder}>Папка
                    <select aria-label="Папка аватаров" value={folderID} disabled={isSaving || isLoading}
                        onChange={event => setFolderID(Number(event.target.value))}>
                        {folders.map(folder => <option key={folder.id} value={folder.id}>{folder.name}</option>)}
                    </select>
                </label>
                {isLoading && <p className={css.catalog_message} role="status">Загрузка каталога…</p>}
                {catalogError && <div className={css.catalog_message}><p role="alert">{catalogError}</p>
                    <Button size="sm" onClick={() => {void loadCatalog(true)}}>Повторить загрузку</Button></div>}

                <div className={css.grid} role="radiogroup" aria-label="Аватары операторов">
                    {items.filter(item => item.folderID === folderID).map(item => <label key={item.id}
                        className={`${css.choice}${selectedID === item.id ? ` ${css.selected}` : ''}`}>
                        <input type="radio" name="operator-avatar" aria-label={`Аватар ${item.id}`}
                            value={item.id} checked={selectedID === item.id} disabled={isSaving || isLoading || Boolean(catalogError)}
                            autoFocus={item.id === selectedID} onChange={() => setSelectedID(item.id)} />
                        <img src={getAvatarCatalogItemUrl(item)} alt="" loading="lazy" />
                        <span aria-hidden="true">{item.id}</span>
                    </label>)}
                </div>

                <div className={css.footer}>
                    <p className={css.selection}>Выбран аватар №{selectedID}</p>
                    {error && <p className={css.error} role="alert">{error}</p>}
                    <div className={css.actions}>
                        <Button variant="primaryDark" padding="0 14px" isDisabled={isSaving} onClick={cancel}>Отмена</Button>
                        <Button padding="0 14px" isLoading={isSaving}
                            isDisabled={isSaving || isLoading || Boolean(catalogError) || !items.some(item => item.id === selectedID) || selectedID === normalizeOperatorAvatarID(avatarID)} onClick={save}>
                            {isSaving ? 'Сохраняем…' : 'Сохранить аватар'}
                        </Button>
                    </div>
                </div>
            </section>
        </Drawer>}
    </>
}
